import { getSupabase } from '../../database';
import type { CategoryViewModel } from '../types';
import { CategoryService } from '../category';

export interface SmartDefaults {
    suggestedCategory: CategoryViewModel | null;
    recentAmounts: number[];
    recentExpenses: RecentExpense[];
}

export interface RecentExpense {
    id: string;
    amount: number;
    currency: string;
    category: CategoryViewModel;
    description?: string;
    date: string;
}

export class QuickEntryService {
    /**
     * Get smart defaults for quick expense entry
     */
    static async getSmartDefaults(
        userId: string,
        theme: 'light' | 'dark'
    ): Promise<SmartDefaults> {
        const now = new Date();
        const hourOfDay = now.getHours();
        const dayOfWeek = now.getDay();

        // Load all data in parallel for performance
        const [suggestedCategory, recentAmounts, recentExpenses] = await Promise.all([
            this.getSuggestedCategory(userId, hourOfDay, dayOfWeek, theme),
            this.getRecentAmounts(userId),
            this.getRecentExpenses(userId, theme),
        ]);

        return {
            suggestedCategory,
            recentAmounts,
            recentExpenses,
        };
    }

    /**
     * Get suggested category based on time patterns
     */
    private static async getSuggestedCategory(
        userId: string,
        hourOfDay: number,
        dayOfWeek: number,
        theme: 'light' | 'dark'
    ): Promise<CategoryViewModel | null> {
        const supabase = getSupabase();

        // Query patterns for current time (+/- 1 hour window)
        const { data, error } = await supabase
            .from('user_expense_patterns')
            .select('category_id, frequency_count')
            .eq('user_id', userId)
            .gte('hour_of_day', hourOfDay - 1)
            .lte('hour_of_day', hourOfDay + 1)
            .eq('day_of_week', dayOfWeek)
            .order('frequency_count', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (error || !data) {
            // Fallback: most recently used category
            const { data: lastExpense } = await supabase
                .from('expenses')
                .select('category_id')
                .eq('user_id', userId)
                .not('category_id', 'is', null)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (lastExpense?.category_id) {
                const category = await CategoryService.getCategoryById(
                    lastExpense.category_id,
                    userId,
                    theme
                );
                return category;
            }

            return null;
        }

        // Load full category with presentation
        const category = await CategoryService.getCategoryById(
            data.category_id,
            userId,
            theme
        );
        return category;
    }

    /**
     * Get recent unique amounts (last 5)
     */
    private static async getRecentAmounts(userId: string): Promise<number[]> {
        const supabase = getSupabase();

        const { data, error } = await supabase
            .from('recent_amounts')
            .select('amount')
            .eq('user_id', userId)
            .order('used_at', { ascending: false })
            .limit(10);

        if (error || !data) return [];

        // Return unique amounts (limit to 5)
        const uniqueAmounts = [...new Set(data.map((r) => r.amount))];
        return uniqueAmounts.slice(0, 5);
    }

    /**
     * Get recent expenses for quick duplication
     */
    private static async getRecentExpenses(
        userId: string,
        theme: 'light' | 'dark'
    ): Promise<RecentExpense[]> {
        const supabase = getSupabase();

        const { data, error } = await supabase
            .from('expenses')
            .select('*')
            .eq('user_id', userId)
            .not('category_id', 'is', null)
            .order('created_at', { ascending: false })
            .limit(5);

        if (error || !data) return [];

        // Enrich with category presentation
        const categories = await Promise.all(
            data.map((exp) =>
                CategoryService.getCategoryById(
                    exp.category_id!,
                    userId,
                    theme
                )
            )
        );

        return data
            .map((exp, index) => {
                const category = categories[index];
                if (!category) return null;
                return {
                    id: exp.id,
                    amount: exp.amount,
                    currency: exp.currency,
                    category,
                    description: exp.description ?? undefined,
                    date: exp.date,
                } as RecentExpense;
            })
            .filter((item): item is RecentExpense => Boolean(item));
    }

}
